import pick from "lodash/pick";
import React, { useCallback, useState } from "react";
import { isNumeric } from '../../../utils/string';
import axiosInstance from "../../../axios";
import { getErrorMessage } from "../../../utils/request";

export const MAX_ALLOWED_QUESTIONS = 20;
export const MAX_ALLOWED_LENGTH = 1000;
export const MIN_ALLOWED_LENGTH = 10;

const QUESTIONS_AI_DEFAULT_PAYLOAD = {
  language: "en_US",
  prompt: "",
  model: "gpt-4o-mini",
  difficulty: "regular",
  numQuestions: 1,
};

const RequestQuestionsAIContext = React.createContext();

function RequestQuestionsAIProvider({ children }) {
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const requestQuestionsAI = useCallback(async (input) => {
    try {
      setLoading(true);
      const payload = getCompletePayload(input);
      const response = await axiosInstance.post(`/questions/ai`, payload);

      return {
        success: true,
        questions: response.data.questions,
        errorMessage: null,
      };
    } catch (error) {
      const errorMessage = getErrorMessage(error, error.message);
      setErrorMessage(errorMessage);
      return { success: false, errorMessage, questions: null };
    } finally {
      setLoading(false);
      setHasLoaded(true);
    }
  }, []);

  const value = {
    loading,
    requestQuestionsAI,
    errorMessage,
    hasLoaded,
  };

  return (
    <RequestQuestionsAIContext.Provider value={value}>
      {children}
    </RequestQuestionsAIContext.Provider>
  );
}

/**
 * @typedef {{
 *  language?: string;
 *  prompt: string;
 *  model?: string,
 *  difficulty?: string,
 *   numQuestions: number
 * }} RequestQuestionsAIPayload
 */

/**
 * @returns {{
 *  loading: boolean,
 *  hasLoaded: boolean,
 *  requestQuestionsAI: (payload: RequestQuestionsAIPayload) => Promise<{ success: boolean; errorMessage: string; }>
 *  errorMessage: string
 * }}
 */

function useRequestQuestionsAI() {
  const context = React.useContext(RequestQuestionsAIContext);

  if (context === undefined) {
    throw new Error(
      "useRequestQuestionsAI must be used within a RequestQuestionsAIProvider",
    );
  }

  return context;
}

function getCompletePayload(partialPayload) {
  const validFields = [
    "language",
    "prompt",
    "model",
    "difficulty",
    "numQuestions",
  ];

  // take only what's necessary
  const payload = {
    ...QUESTIONS_AI_DEFAULT_PAYLOAD,
    ...pick(partialPayload, validFields)
  };

  // convert numeric string
  payload.numQuestions = parseInt(payload.numQuestions);

  validatePayload(payload);

  return {
    ...QUESTIONS_AI_DEFAULT_PAYLOAD,
    ...payload,
  };
}

function validatePayload(partialPayload) {
  const { prompt: input, numQuestions } = partialPayload;

  if (
    typeof input !== "string" ||
    input.length < MIN_ALLOWED_LENGTH ||
    input.length > MAX_ALLOWED_LENGTH
  ) {
    throw new Error(
      `Prompt text: Must be a text between ${MIN_ALLOWED_LENGTH} and ${MAX_ALLOWED_LENGTH} characters long`,
    );
  }

  const number = parseInt(numQuestions);
  if (!isNumeric(numQuestions) || number <= 0) {
    throw new Error("Number of questions: Must be a positive number");
  }
}

export { RequestQuestionsAIProvider, useRequestQuestionsAI };
